import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  FilePlus2,
  Hourglass,
  Info,
  RotateCcw,
  Save,
  Send,
  ShieldCheck,
  Wrench,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../../components/context/AuthContext";
import { dofWorkflowAction } from "../../../api/kalite/dofApi";
import { getPersonel } from "../../../api/authApi";
import DofPersonelSelect from "./DofPersonelSelect";
import {
  DOF_STATUSES,
  ASSIGNMENT_LABELS,
  boolVal,
  formatDofDate,
  toDateInputValue,
  todayInputValue,
} from "./dofFormUtils";

const STEP_ICONS = { acik: Wrench, onay_bekliyor: Hourglass, takipte: ShieldCheck, kapali: BadgeCheck };

const NOTE_ACTION_LABELS = {
  reject: "Onaycı faaliyetleri reddetti",
  return: "Takipte faaliyetler etkin bulunmadı",
  reopen: "DÖF yeniden açıldı",
};

const FAALIYETLER = [
  { type: "duzetme", label: "Düzeltme", hint: "Uygunsuzluğa anlık müdahale", tanim: "duzetmeFaliyetTanimi", sorumlu: "duzeltmeSorumlu", termin: "duzeltmeTerminSure" },
  { type: "duzeltici", label: "Düzeltici Faaliyet", hint: "Kök nedeni ortadan kaldıran faaliyet", tanim: "duzelticiFaliyetTanimi", sorumlu: "duzelticiSorumlu", termin: "duzelticiTerminSure" },
  { type: "onleyici", label: "Önleyici Faaliyet", hint: "Tekrarını / benzerini önleyen faaliyet", tanim: "onleyiciciFaliyetTanimi", sorumlu: "onleyiciciSorumlu", termin: "onleyiciciTerminSure" },
];

const getVisibleFaaliyetler = (faliyetTuru) => {
  const duzetme = FAALIYETLER.find((faaliyet) => faaliyet.type === "duzetme");
  const selected = FAALIYETLER.find((faaliyet) => faaliyet.type === faliyetTuru);
  return selected && selected.type !== "duzetme" ? [duzetme, selected] : FAALIYETLER;
};

const REVIZYONLAR = [
  { key: "prosedur", label: "Prosedür" },
  { key: "talimat", label: "Talimat" },
  { key: "isAkisi", label: "İş Akışı" },
  { key: "form", label: "Form" },
];

const pickFields = (dof, keys) =>
  keys.reduce((acc, key) => {
    acc[key] = dof?.[key] ?? "";
    return acc;
  }, {});

const SORUMLU_KEYS = ["kokNeden", ...FAALIYETLER.flatMap((f) => [f.tanim, f.sorumlu, f.termin])];
const TAKIP_KEYS = [
  "takipTarihi",
  "takibiGerceklestiren",
  "takipSonucuOneri",
  "yeniDofAcilmali",
  "yonetimTemsilcisi",
  ...REVIZYONLAR.flatMap((r) => [r.key, `${r.key}RevTarihi`, `${r.key}RevNo`]),
  "digerRevAciklama",
  "digerRevTarihi",
  "digerRevNo",
];

export function DofStepper({ status }) {
  const statusIndex = DOF_STATUSES.findIndex((s) => s.key === status);
  return (
    <div className="dof-stepper">
      {DOF_STATUSES.map((s, index) => {
        const Icon = STEP_ICONS[s.key];
        const done = index < statusIndex || status === "kapali";
        const active = index === statusIndex && status !== "kapali";
        return (
          <div key={s.key} className={`dof-step ${done ? "done" : ""} ${active ? "active" : ""}`}>
            <span className="dof-step-dot">{done ? <Check size={16} /> : <Icon size={16} />}</span>
            <span>{s.long}</span>
          </div>
        );
      })}
    </div>
  );
}

function DofWorkflowPanel({ dof, access, onChanged }) {
  const navigate = useNavigate();
  const { user, permissions } = useAuth();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [sorumluData, setSorumluData] = useState(() => pickFields(dof, SORUMLU_KEYS));
  const [takipData, setTakipData] = useState(() => pickFields(dof, TAKIP_KEYS));
  const [personelLoading, setPersonelLoading] = useState(false);
  const [personelList, setPersonelList] = useState([]);
  const canEditSorumlu = Boolean(access?.actions?.save || access?.actions?.submit);

  useEffect(() => {
    setSorumluData(pickFields(dof, SORUMLU_KEYS));
    const takip = pickFields(dof, TAKIP_KEYS);
    if (!takip.takipTarihi) takip.takipTarihi = todayInputValue();
    if (!takip.takibiGerceklestiren && user) {
      takip.takibiGerceklestiren = `${user.name || ""} ${user.surname || ""}`.trim();
    }
    setTakipData(takip);
    setNote("");
  }, [dof, user]);

  useEffect(() => {
    if (!canEditSorumlu) return;

    const fetchPersonel = async () => {
      setPersonelLoading(true);
      try {
        const response = await getPersonel();
        const list = response?.data?.data || response?.data?.results || response?.data || [];
        setPersonelList(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error("DÖF faaliyet sorumluları alınamadı:", error);
        setPersonelList([]);
      } finally {
        setPersonelLoading(false);
      }
    };

    fetchPersonel();
  }, [canEditSorumlu]);

  const status = dof?.status || "acik";
  const actions = access?.actions || {};
  const hasAnyAction = Object.values(actions).some(Boolean);
  const visibleFaaliyetler = getVisibleFaaliyetler(dof?.faliyetTuru);

  const getSorumluPayload = () => {
    const keys = ["kokNeden", ...visibleFaaliyetler.flatMap((f) => [f.tanim, f.sorumlu, f.termin])];
    return pickFields(sorumluData, keys);
  };

  const onSorumluChange = (e) => {
    const { name, value } = e.target;
    setSorumluData((prev) => ({ ...prev, [name]: value }));
  };

  const onTakipChange = (e) => {
    const { name, value } = e.target;
    const parsed = value === "true" ? true : value === "false" ? false : value;
    setTakipData((prev) => ({ ...prev, [name]: parsed }));
  };

  const cleanBooleans = (data) => {
    const result = { ...data };
    ["yeniDofAcilmali", ...REVIZYONLAR.map((r) => r.key)].forEach((key) => {
      if (result[key] === "") delete result[key];
    });
    return result;
  };

  const run = async (action, { data, confirmText, requireNote } = {}) => {
    if (requireNote && !note.trim()) {
      alert("Lütfen açıklama giriniz.");
      return;
    }
    if (confirmText && !window.confirm(confirmText)) return;

    setBusy(true);
    try {
      const response = await dofWorkflowAction(dof._id, action, { note: note.trim(), data });
      alert(response?.data?.message || "İşlem tamamlandı.");
      if (
        action === "close" &&
        data?.yeniDofAcilmali === true &&
        permissions.includes("dof_set") &&
        window.confirm("Yeni DÖF açılmalı olarak işaretlediniz. Şimdi bu kayıttan yeni bir DÖF açmak ister misiniz?")
      ) {
        navigate(`/DofiWizard?kaynak=${dof._id}`);
        return;
      }
      onChanged && onChanged();
    } catch (error) {
      console.error("DÖF iş akışı hatası:", error);
      alert(error.response?.data?.message || "İşlem sırasında bir hata oluştu.");
    } finally {
      setBusy(false);
    }
  };

  const noteField = (label, placeholder) => (
    <div className="dof-field">
      <label>{label}</label>
      <textarea className="dof-control" rows={2} value={note} placeholder={placeholder} onChange={(e) => setNote(e.target.value)} />
    </div>
  );

  const roleNames = (type) =>
    (dof?.assignedUsers || [])
      .filter((u) => u.assignmentType === type)
      .map((u) => u.fullName)
      .join(", ");

  const waitingText = {
    acik: `Sorumlunun kök neden ve faaliyetleri girip onaya göndermesi bekleniyor${roleNames("sorumlu") ? ` (${roleNames("sorumlu")})` : ""}.`,
    onay_bekliyor: `Onaycının faaliyetleri değerlendirmesi bekleniyor${roleNames("onayci") ? ` (${roleNames("onayci")})` : " (onaycı ataması eksik)"}.`,
    takipte: `Takipçinin etkinlik takibini yapıp DÖF'ü kapatması bekleniyor${roleNames("takipci") ? ` (${roleNames("takipci")})` : " (takipçi ataması eksik)"}.`,
    kapali: `DÖF ${formatDofDate(dof?.dofKapatmaTarihiOnay)} tarihinde kapatıldı.`,
  }[status];
  const activeRole = { acik: "sorumlu", onay_bekliyor: "onayci", takipte: "takipci" }[status];

  return (
    <section className="dof-panel">
      <div className="dof-panel-header">
        <div>
          <h2>
            <ShieldCheck size={18} /> İş Akışı
          </h2>
          <p>{waitingText}</p>
        </div>
      </div>
      <div className="dof-panel-body">
        <DofStepper status={status} />
        <div style={{ height: 18 }} />

        {access?.myTurn && (
          <div className="dof-callout turn">
            <Info size={17} />
            <div>
              <b>Sıra sizde.</b> Bu DÖF'te {ASSIGNMENT_LABELS[activeRole] || "ilgili"} olarak işlem yapmanız bekleniyor.
            </div>
          </div>
        )}

        {access?.fullAccess && !access?.myTurn && hasAnyAction && status !== "kapali" && (
          <div className="dof-callout info">
            <ShieldCheck size={17} />
            <div>
              <b>Tam yetki.</b> Bu adıma atanmış olmasanız da DÖF tam yetkiniz ile işlem yapabilirsiniz. Yaptığınız işlem süreç geçmişine "tam yetki ile" olarak
              kaydedilir.
            </div>
          </div>
        )}

        {dof?._termin?.overdue && (
          <div className="dof-callout danger">
            <AlertTriangle size={17} />
            <div>Faaliyet termin tarihi geçmiş durumda. Lütfen faaliyetleri tamamlayıp onaya gönderin.</div>
          </div>
        )}
        {status === "takipte" && dof?._termin?.followUpDue && (
          <div className={`dof-callout ${dof._termin.followUpLate ? "danger" : "info"}`}>
            <AlertTriangle size={17} />
            <div>
              {dof._termin.followUpLate
                ? "Faaliyet terminlerinin üzerinden 7 günden fazla geçti; etkinlik takibi gecikti."
                : "Faaliyet terminleri doldu; etkinlik takibi yapılıp DÖF kapatılabilir."}
            </div>
          </div>
        )}

        {dof?.lastNote?.note && status === "acik" && NOTE_ACTION_LABELS[dof.lastNote.action] && (
          <div className="dof-callout warn">
            <RotateCcw size={17} />
            <div style={{ whiteSpace: "pre-wrap" }}>
              <b>
                {NOTE_ACTION_LABELS[dof.lastNote.action]}
                {dof.lastNote.fullName ? ` — ${dof.lastNote.fullName}` : ""}
              </b>
              <br />
              {dof.lastNote.note}
            </div>
          </div>
        )}

        {/* 1) Sorumlu */}
        {(actions.save || actions.submit) && (
          <>
            <div className="dof-field">
              <label>Kök Neden *</label>
              <textarea
                name="kokNeden"
                className="dof-control"
                rows={3}
                value={sorumluData.kokNeden}
                onChange={onSorumluChange}
                placeholder="Uygunsuzluğun kök nedeni (ör. 5 Neden / Balık Kılçığı analizi sonucu)"
              />
            </div>
            {visibleFaaliyetler.map((f) => (
              <div className="dof-subcard" key={f.tanim}>
                <h5>
                  {f.label} <span className="dof-sub" style={{ margin: 0, fontWeight: 600 }}>· {f.hint}</span>
                </h5>
                <div className="dof-field">
                  <textarea name={f.tanim} className="dof-control" rows={2} value={sorumluData[f.tanim]} onChange={onSorumluChange} placeholder="Faaliyet tanımı" />
                </div>
                <div className="dof-field-row" style={{ gridTemplateColumns: "2fr 1fr" }}>
                  <div className="dof-field">
                    <label>Faaliyet Sorumlusu</label>
                    <DofPersonelSelect
                      personelList={personelList}
                      value={sorumluData[f.sorumlu]}
                      onChange={(value) => setSorumluData((prev) => ({ ...prev, [f.sorumlu]: value }))}
                      loading={personelLoading}
                      disabled={busy}
                    />
                  </div>
                  <div className="dof-field">
                    <label>Termin Tarihi</label>
                    <input type="date" name={f.termin} className="dof-control" value={toDateInputValue(sorumluData[f.termin])} onChange={onSorumluChange} />
                  </div>
                </div>
              </div>
            ))}
            <span className="dof-hint" style={{ marginBottom: 12 }}>
              Düzeltme uygulanabiliyorsa kaydedin. Seçilen düzeltici veya önleyici faaliyet için tanım, sorumlu ve termin zorunludur.
            </span>
            <div className="dof-actions">
              {actions.save && (
                <button type="button" className="dof-btn dof-btn-secondary" disabled={busy} onClick={() => run("save", { data: getSorumluPayload() })}>
                  <Save size={16} /> Taslak Kaydet
                </button>
              )}
              {actions.submit && (
                <button
                  type="button"
                  className="dof-btn dof-btn-primary"
                  disabled={busy}
                  onClick={() => run("submit", { data: getSorumluPayload(), confirmText: "Faaliyetler onaya gönderilsin mi?" })}
                >
                  <Send size={16} /> {busy ? "Gönderiliyor..." : "Onaya Gönder"}
                </button>
              )}
            </div>
          </>
        )}

        {/* 2) Onaycı */}
        {(actions.approve || actions.reject) && (
          <>
            <div className="dof-callout info">
              <Info size={17} />
              <div>Aşağıdaki kök neden ve faaliyetleri inceleyin. Reddederseniz DÖF açıklamanızla birlikte sorumluya geri döner.</div>
            </div>
            {noteField("Açıklama (reddetmek için zorunlu)", "Onay notu ya da red gerekçesi")}
            <div className="dof-actions">
              {actions.reject && (
                <button
                  type="button"
                  className="dof-btn dof-btn-danger"
                  disabled={busy}
                  onClick={() => run("reject", { requireNote: true, confirmText: "Faaliyetler reddedilip sorumluya geri gönderilsin mi?" })}
                >
                  <XCircle size={16} /> Reddet
                </button>
              )}
              {actions.approve && (
                <button type="button" className="dof-btn dof-btn-success" disabled={busy} onClick={() => run("approve", { confirmText: "Faaliyetler onaylansın mı?" })}>
                  <BadgeCheck size={16} /> {busy ? "İşleniyor..." : "Onayla"}
                </button>
              )}
            </div>
          </>
        )}

        {/* 3) Takipçi */}
        {(actions.close || actions.return) && (
          <>
            <div className="dof-field-row">
              <div className="dof-field">
                <label>Takip Tarihi</label>
                <input type="date" name="takipTarihi" className="dof-control" value={toDateInputValue(takipData.takipTarihi)} onChange={onTakipChange} />
              </div>
              <div className="dof-field">
                <label>Takibi Gerçekleştiren</label>
                <input name="takibiGerceklestiren" className="dof-control" value={takipData.takibiGerceklestiren} onChange={onTakipChange} />
              </div>
              <div className="dof-field">
                <label>Yönetim Temsilcisi</label>
                <input name="yonetimTemsilcisi" className="dof-control" value={takipData.yonetimTemsilcisi} onChange={onTakipChange} />
              </div>
            </div>
            <div className="dof-field">
              <label>Takip Sonucu &amp; Öneri *</label>
              <textarea
                name="takipSonucuOneri"
                className="dof-control"
                rows={3}
                value={takipData.takipSonucuOneri}
                onChange={onTakipChange}
                placeholder="Faaliyetler uygulandı mı, uygunsuzluk tekrar etti mi, etkinlik nasıl doğrulandı?"
              />
            </div>

            <div className="dof-subcard">
              <h5>Doküman Revizyonları</h5>
              {REVIZYONLAR.map((r) => (
                <div className="dof-field-row" key={r.key} style={{ marginBottom: 8 }}>
                  <select name={r.key} className="dof-control" value={boolVal(takipData[r.key])} onChange={onTakipChange}>
                    <option value="">{r.label} değişecek mi?</option>
                    <option value="true">{r.label}: Evet</option>
                    <option value="false">{r.label}: Hayır</option>
                  </select>
                  {takipData[r.key] === true ? (
                    <>
                      <input type="date" name={`${r.key}RevTarihi`} className="dof-control" value={toDateInputValue(takipData[`${r.key}RevTarihi`])} onChange={onTakipChange} title="Rev. tarihi" />
                      <input name={`${r.key}RevNo`} className="dof-control" placeholder="Rev. No" value={takipData[`${r.key}RevNo`]} onChange={onTakipChange} />
                    </>
                  ) : (
                    <>
                      <span />
                      <span />
                    </>
                  )}
                </div>
              ))}
              <div className="dof-field-row">
                <input name="digerRevAciklama" className="dof-control" placeholder="Diğer doküman" value={takipData.digerRevAciklama} onChange={onTakipChange} />
                <input type="date" name="digerRevTarihi" className="dof-control" value={toDateInputValue(takipData.digerRevTarihi)} onChange={onTakipChange} />
                <input name="digerRevNo" className="dof-control" placeholder="Rev. No" value={takipData.digerRevNo} onChange={onTakipChange} />
              </div>
            </div>

            <div className="dof-field" style={{ maxWidth: 320 }}>
              <label>Yeni DÖF Açılmalı mı?</label>
              <select name="yeniDofAcilmali" className="dof-control" value={boolVal(takipData.yeniDofAcilmali)} onChange={onTakipChange}>
                <option value="">Seçim Yapınız</option>
                <option value="true">Evet</option>
                <option value="false">Hayır</option>
              </select>
            </div>

            {noteField("Açıklama (geri göndermek için zorunlu)", "Etkin bulunmadıysa sorumluya iletilecek açıklama")}

            <div className="dof-actions">
              {actions.return && (
                <button
                  type="button"
                  className="dof-btn dof-btn-danger"
                  disabled={busy}
                  onClick={() =>
                    run("return", {
                      data: cleanBooleans(takipData),
                      requireNote: true,
                      confirmText: "Faaliyetler etkin bulunmadı olarak işaretlenip DÖF sorumluya geri gönderilsin mi?",
                    })
                  }
                >
                  <RotateCcw size={16} /> Etkin Değil – Geri Gönder
                </button>
              )}
              {actions.close && (
                <button
                  type="button"
                  className="dof-btn dof-btn-success"
                  disabled={busy}
                  onClick={() => {
                    if (!String(takipData.takipSonucuOneri || "").trim()) {
                      alert("Kapatmak için takip sonucu / öneri giriniz.");
                      return;
                    }
                    run("close", {
                      data: cleanBooleans(takipData),
                      confirmText: "Faaliyetler etkin bulundu, DÖF kapatılsın mı? Kapatma tarihi bugün olarak atanacak.",
                    });
                  }}
                >
                  <BadgeCheck size={16} /> {busy ? "İşleniyor..." : "Etkin – DÖF'ü Kapat"}
                </button>
              )}
            </div>
          </>
        )}

        {/* 4) Kapalı */}
        {actions.reopen && (
          <>
            {noteField("Yeniden açma gerekçesi", "Neden yeniden açılıyor?")}
            <div className="dof-actions">
              {permissions.includes("dof_set") && !dof?.yeniDofId && (
                <button type="button" className="dof-btn dof-btn-secondary" disabled={busy} onClick={() => navigate(`/DofiWizard?kaynak=${dof._id}`)}>
                  <FilePlus2 size={16} /> Bu Kayıttan Yeni DÖF Aç
                </button>
              )}
              <button
                type="button"
                className="dof-btn dof-btn-warning"
                disabled={busy}
                onClick={() => run("reopen", { requireNote: true, confirmText: "Kapalı DÖF yeniden açılsın mı?" })}
              >
                <RotateCcw size={16} /> Yeniden Aç
              </button>
            </div>
          </>
        )}

        {!hasAnyAction && status !== "kapali" && (
          <span className="dof-hint">Bu adımda sizin yapmanız gereken bir işlem yok.</span>
        )}
      </div>
    </section>
  );
}

export default DofWorkflowPanel;
